// src/pages/builder/SeasonDatesPanel.js
//
// Layout Builder ▸ 🗓 Season dates. The 12 holiday seasons of the shop and
// avatar maker each have a window when their things can be BOUGHT (anyone
// can try them on all year). Here an admin can, per season:
//   • move the dates (every year, "from" → "until", may wrap New Year)
//   • open it now, whatever the date — a class party, a surprise
//   • close it for this year
//   • reset it to the built-in dates
// Saved to rewardSettings/seasons → every shop and avatar maker changes
// straight away, and the server checks the same thing when someone buys.

import React, { useEffect, useState } from "react";
import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { SEASONS, niceDay, seasonOpen, setSeasonOverrides } from "../../components/avatar/seasons/seasonData";
import FunPopup from "../../components/common/FunPopup";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const pad = (n) => String(n).padStart(2, "0");

const DayPick = ({ value, onChange, label }) => {
  const [m, d] = String(value || "01-01").split("-").map(Number);
  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      <small style={{ fontWeight: 800, color: "#6b7280" }}>{label}</small>
      <select value={m} onChange={(e) => onChange(`${pad(e.target.value)}-${pad(d)}`)} aria-label={`${label} month`}>
        {MONTHS.map((x, i) => (
          <option key={x} value={i + 1}>
            {x}
          </option>
        ))}
      </select>
      <select value={d} onChange={(e) => onChange(`${pad(m)}-${pad(e.target.value)}`)} aria-label={`${label} day`}>
        {DAYS.map((x) => (
          <option key={x} value={x}>
            {x}
          </option>
        ))}
      </select>
    </span>
  );
};

const SeasonDatesPanel = () => {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState({}); // what's in Firestore
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [pop, setPop] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    return onSnapshot(
      doc(db, "rewardSettings", "seasons"),
      (snap) => {
        const s = (snap.exists() && snap.data().seasons) || {};
        setSaved(s);
        setDraft(s);
      },
      () => {}
    );
  }, [open]);

  const rowOf = (s) => {
    const o = draft[s.id] || {};
    return { mode: o.mode || "dates", from: o.from || s.from, until: o.until || s.until };
  };
  const setRow = (id, patch) => {
    const s = SEASONS.find((x) => x.id === id);
    setDraft((d) => ({ ...d, [id]: { ...rowOf(s), ...patch } }));
  };
  const reset = (id) =>
    setDraft((d) => {
      const n = { ...d };
      delete n[id];
      return n;
    });

  // what a season WOULD be with the draft (for the "on sale now?" column)
  const wouldBeOpen = (id) => {
    setSeasonOverrides(draft);
    const r = seasonOpen(id);
    setSeasonOverrides(saved);
    return r;
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  const save = async () => {
    setBusy(true);
    try {
      // only keep seasons that differ from the built-in dates
      const clean = {};
      SEASONS.forEach((s) => {
        const r = draft[s.id];
        if (!r) return;
        if ((r.mode || "dates") === "dates" && r.from === s.from && r.until === s.until) return;
        clean[s.id] = { mode: r.mode || "dates", from: r.from || s.from, until: r.until || s.until };
      });
      await setDoc(doc(db, "rewardSettings", "seasons"), { seasons: clean, updatedAt: serverTimestamp() });
      setPop({ tone: "ok", title: "Seasons saved!", text: "Every shop and avatar maker uses the new dates right now." });
    } catch (e) {
      setPop({ tone: "bad", title: "Couldn't save", text: e?.message || "Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="lb-card">
      <div className="lb-card-head">
        <span className="lb-step">🗓</span>
        <div>
          <h2>Season dates</h2>
          <p className="lb-hint">When each holiday's hats, outfits and pets can be bought. Kids can try them on any time.</p>
        </div>
        <button type="button" className="lb-btn" onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : "Open"}
        </button>
      </div>

      {open && (
        <div style={{ display: "grid", gap: 8 }}>
          {SEASONS.map((s) => {
            const r = rowOf(s);
            const nowOpen = wouldBeOpen(s.id);
            const changed = Boolean(draft[s.id]);
            return (
              <div
                key={s.id}
                style={{
                  display: "flex", flexWrap: "wrap", gap: "8px 14px", alignItems: "center", padding: "10px 12px", borderRadius: 14,
                  background: nowOpen ? "#ecfdf5" : "#fff", border: `2px solid ${nowOpen ? "#86efac" : "#ede9fe"}`,
                }}
              >
                <strong style={{ minWidth: 170, fontSize: 16 }}>
                  {s.emoji} {s.name}
                </strong>
                <select value={r.mode} onChange={(e) => setRow(s.id, { mode: e.target.value })} aria-label={`${s.name} — when`}>
                  <option value="dates">On sale between dates</option>
                  <option value="open">Open now (any date)</option>
                  <option value="closed">Closed this year</option>
                </select>
                {r.mode === "dates" && (
                  <>
                    <DayPick label="from" value={r.from} onChange={(v) => setRow(s.id, { from: v })} />
                    <DayPick label="until" value={r.until} onChange={(v) => setRow(s.id, { until: v })} />
                  </>
                )}
                <span style={{ fontWeight: 800, color: nowOpen ? "#15803d" : "#6b7280" }}>{nowOpen ? "✅ On sale now" : "Not on sale now"}</span>
                {changed && (
                  <button type="button" className="lb-btn lb-btn-soft" onClick={() => reset(s.id)} title={`Back to ${niceDay(s.from)} – ${niceDay(s.until)}`}>
                    ↺ Built-in dates
                  </button>
                )}
              </div>
            );
          })}
          <div>
            <button type="button" className="lb-btn" onClick={save} disabled={busy || !dirty}>
              {busy ? "Saving…" : dirty ? "Save season dates" : "Saved"}
            </button>
            <span className="lb-hint" style={{ marginLeft: 10 }}>
              {Object.keys(saved).length ? `${Object.keys(saved).length} season(s) changed from the built-in dates.` : "All seasons use the built-in dates."}
            </span>
          </div>
        </div>
      )}
      <FunPopup pop={pop} onClose={() => setPop(null)} />
    </section>
  );
};

export default SeasonDatesPanel;
